import React, { useCallback, useEffect, useState } from 'react';
import RootNavigation from './navigation';
import { GetUserInformationUseCase } from './useCases/HybrisAuthenticateUseCases/GetUserInformationUseCase';
import { LocalStorage } from './storage/LocalStorage';
import { useUserInfoStore, type UserType } from './stores/userInfo';
import { Toast } from './components';
import { AppOriginType, MarisaAppUtils } from './utils/MarisaAppUtils';
import { USER_INFO } from '~/storage/constants';
import { Linking, Platform } from 'react-native';
import useAppNavigation from './hooks/useAppNavigation';
import {
  MARISA_CONSORTIUM_APP_LINK,
  MARISA_CONSORTIUM_TOTEM_LINK,
  MARISA_CONSORTIUM_WEB_LINK,
} from './utils/url';
import { isWeb } from './utils/device';
import { useWebIdler } from './hooks/useWebIdler';
import Loading from './components/Loading';
import initCredsystemUser from './credsystem/hooks/initCredsystemUser';

export interface MarisaServicesProps {
  authToken?: string;
  logout?: () => Promise<void>;
  goToLogin?: () => void;
  goToMbank?: () => void;
  disableMbank?: boolean;
  goToStoresList?: () => void;
  copyToClipboard?: (value: string) => Promise<void | boolean>;
  shareItem?: (item: string) => Promise<void | boolean>;
  onlyUseExternalToken?: boolean;
  origin?: AppOriginType;
  disableConsortium?: boolean;
  disableInsurance?: boolean;
  //CREDSYSTEM
  deviceId: string;
  OSId: string;
  OSVersion: string;
  deviceModel: string;
}

export const MarisaServices = ({
  authToken,
  logout,
  goToLogin,
  goToMbank,
  disableMbank,
  goToStoresList,
  copyToClipboard,
  shareItem,
  onlyUseExternalToken,
  origin = isWeb ? AppOriginType.CREDIT : AppOriginType.APP,
  deviceId,
  deviceModel,
  OSId,
  OSVersion,
  disableConsortium,
  disableInsurance,
}: MarisaServicesProps) => {
  const { navigate } = useAppNavigation();
  const [authLoading, setAuthLoading] = useState(true);
  useWebIdler(origin === AppOriginType.TOTEM);
  const authenticationRoutine = useCallback(async () => {
    if (authToken) {
      if (useUserInfoStore.getState().user?.token === authToken) return;
      try {
        await GetUserInformationUseCase(authToken);
      } catch (error: any) {
        useUserInfoStore.getState().deleteUserInfo();

        // TODO onde mostrar os erros
        if (error?.message) console.log(error.message);
      }
    } else {
      if (onlyUseExternalToken) {
        useUserInfoStore.getState().deleteUserInfo();
      } else {
        const userInfo = await LocalStorage.get<UserType>(USER_INFO);

        if (userInfo) {
          useUserInfoStore.setState({ user: userInfo });
        }
      }
    }

    useUserInfoStore.getState().loadContractedServices();
  }, [authToken, onlyUseExternalToken]);

  const configMarisaAppUtils = useCallback(() => {
    MarisaAppUtils.origin = origin;
    MarisaAppUtils.disableConsortium = disableConsortium || false;
    MarisaAppUtils.disableInsurance = disableInsurance || false;
    const isTotem = MarisaAppUtils.isTotem();
    MarisaAppUtils.openUrl = (url: string) => {
      if (isWeb && isTotem) {
        navigate('MarisaServicos_Iframe', { url });
      } else {
        Linking.openURL(url);
      }
    };
    MarisaAppUtils.goToLogin = () => {
      if (goToLogin) {
        goToLogin();
      } else {
        navigate('MarisaServicos_SignIn');
      }
    };
    MarisaAppUtils.goToMbank = () => {
      if (goToMbank) {
        goToMbank();
      } else {
        MarisaAppUtils.openUrl('https://portalcliente.cldmar.com.br');
      }
    };
    MarisaAppUtils.disableMbank = !!disableMbank;

    MarisaAppUtils.goToStoresList = () => goToStoresList?.();
    MarisaAppUtils.goToConsortium = () => {
      let url = Platform.select({
        web: MARISA_CONSORTIUM_WEB_LINK,
        default: MARISA_CONSORTIUM_APP_LINK,
      });
      if (isTotem) {
        url = MARISA_CONSORTIUM_TOTEM_LINK;
      }
      MarisaAppUtils.openUrl(url);
    };
    MarisaAppUtils.copyToClipboard = copyToClipboard;
    MarisaAppUtils.shareItem = shareItem;
  }, [
    goToStoresList,
    copyToClipboard,
    disableConsortium,
    goToLogin,
    goToMbank,
    navigate,
    origin,
    shareItem,
    disableInsurance,
    disableMbank,
  ]);

  const configMarisaAppUtilsCredsystem = useCallback(() => {
    MarisaAppUtils.deviceId = deviceId;
    MarisaAppUtils.deviceModel = deviceModel;
    MarisaAppUtils.OSId = OSId;
    MarisaAppUtils.OSVersion = OSVersion;
  }, [OSId, OSVersion, deviceId, deviceModel]);

  useEffect(() => {
    initCredsystemUser();
  }, []);

  useEffect(() => {
    useUserInfoStore.setState({ customLogout: logout });
  }, [logout]);

  useEffect(() => {
    authenticationRoutine().finally(() => setAuthLoading(false));
  }, [authenticationRoutine]);

  useEffect(() => {
    configMarisaAppUtils();
  }, [configMarisaAppUtils]);

  useEffect(() => {
    configMarisaAppUtilsCredsystem();
  }, [configMarisaAppUtilsCredsystem]);

  return (
    <>
      <Loading visible={authLoading} />
      {!authLoading && <RootNavigation />}
      <Toast />
    </>
  );
};
